import React, { useContext, useState, useEffect, Fragment } from 'react';
import  { ProductContext } from '../../context/products-context';
import { CartItemProps } from '../../models/product';
import { Card, Grid, Alert, Divider, Typography, Container, Box, CircularProgress, useTheme, useMediaQuery } from '@mui/material';
import CartItem from '../../components/CartItem';
import EmptyCart from '../../components/EmptyCart';
import SendOrderForm from '../../components/SendOrderForm';
import Header from '../../components/Header';

const SEND_ORDER_FORM_ID = 'send-order-form';

const Cart = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { cartItems, products, isLoading, productsError, clearCart } = useContext(ProductContext);

  const productsInCart = products.filter(({ name }) => cartItems[name]);
  const cartItemsDetails: CartItemProps[] = productsInCart.map(product => ({
    ...product,
    quantity: cartItems[product.name]
  }));

  const getTotal = () => (
    cartItemsDetails.reduce((total, { price, quantity }) => (
      total + (price * quantity)
    ), 0)
  );

  const [toast, setToast] = useState<{ show: boolean; severity: 'success' | 'error'; message: string }>({
    show: false,
    severity: 'success',
    message: '',
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!toast.show) return;
    const t = setTimeout(() => setToast(prev => ({ ...prev, show: false })), 3000);
    return () => clearTimeout(t);
  }, [toast.show]);

  return (
    <Container sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }} maxWidth={false} disableGutters>
      <Header/>
      <Container component='main' sx={{ flexGrow: 1, mt: '80px', display: 'flex', flexDirection: 'column' }} maxWidth={false}>
        {toast.show && (
          <Box sx={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 1400, minWidth: 320 }}>
            <Alert
              onClose={() => setToast(prev => ({ ...prev, show: false }))}
              severity={toast.severity}
            >
              {toast.message}
            </Alert>
          </Box>
        )}
        {productsError && (
          <Alert severity="error" sx={{ mx: 2, mb: 2 }}>
            {productsError}
          </Alert>
        )}
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : productsInCart.length === 0 ? <EmptyCart /> :
          <>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, flex: 1, pb: isMobile ? '380px' : 0 }}>
              <Grid container spacing={0} sx={{ flex: { md: 1 } }}>
                <Grid size={{ xs: 12, md: 8 }}>
                  <Grid container spacing={2} alignItems='center' sx={{ opacity: 0.7, mx: 2, display: { xs: 'none', md: 'flex' } }}>
                    <Grid size={2}/>
                    <Grid size={3} sx={{ px: 2 }}>
                      <Typography fontSize={24} variant="subtitle1" sx={{ px: 1 }}>Product</Typography>
                    </Grid>
                    <Grid size={1} sx={{ pr: 2 }}>
                      <Typography fontSize={24} variant="subtitle1">Price</Typography>
                    </Grid>
                    <Grid size={3} sx={{ display: 'flex', justifyContent: 'center' }}>
                      <Typography fontSize={24} variant="subtitle1" align='center'>Quantity</Typography>
                    </Grid>
                    <Grid size={2} sx={{ pl: 2 }}>
                      <Typography fontSize={24} variant="subtitle1">Total</Typography>
                    </Grid>
                    <Grid size={1}/>
                  </Grid>
                  <Divider sx={{ opacity: 0.8, mx: 2, display: { xs: 'none', md: 'block' } }}/>
                  {cartItemsDetails.map((item, index) => (
                    <Fragment key={item.name}>
                      <CartItem {...item} />
                      {index < cartItemsDetails.length - 1 && <Divider key={`divider-${index}`} sx={{ opacity: 0.8, mx: 2 }}/>}
                    </Fragment>
                  ))}
                </Grid>
                {!isMobile && (
                  <Grid size={4} sx={{ position: 'sticky', top: 80, alignSelf: 'flex-start', height: 'fit-content' }}>
                    <Card elevation={0} sx={{ boxShadow: 0, border: 'none' }}>
                      <SendOrderForm getTotal={getTotal} clearCart={clearCart} setToast={setToast} formId={SEND_ORDER_FORM_ID} layout="desktop" />
                    </Card>
                  </Grid>
                )}
              </Grid>
              {isMobile && (
                <Box
                  sx={{
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    zIndex: 1100,
                    width: '100%',
                    backgroundColor: 'background.paper',
                  }}
                >
                  <SendOrderForm getTotal={getTotal} clearCart={clearCart} setToast={setToast} formId={SEND_ORDER_FORM_ID} layout="mobile" />
                </Box>
              )}
            </Box>
          </>
        }
      </Container>
    </Container>
  );
};

export default Cart;